import { useCallback, useEffect, useMemo, useState } from "react";
import useWeeklyPlanApi from "../api/use-weekly-plan-api";
import { BrandDetails } from "../types/add-brand-types";
import { WeeklyPlan } from "../types/weekly-plan";
import { formatWeekStart } from "../utils/week-plan-date";
import { WEEKLY_PLAN_REFRESH_EVENT } from "../utils/weekly-plan-events";

export type WeeklyPlansByBrandId = Record<string, WeeklyPlan | null | undefined>;

const useWeeklyPlansForBrands = (
  brands: BrandDetails[] | null | undefined
) => {
  const { getWeeklyPlan } = useWeeklyPlanApi();
  const [plansByBrandId, setPlansByBrandId] = useState<WeeklyPlansByBrandId>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const brandIds = useMemo(
    () => (brands ?? []).map((brand) => brand.id).filter(Boolean),
    [brands]
  );
  const brandIdsKey = brandIds.join("|");
  const weekStart = useMemo(() => formatWeekStart(), []);

  const refreshPlans = useCallback(async () => {
    const ids = brandIdsKey ? brandIdsKey.split("|") : [];
    if (ids.length === 0) {
      setPlansByBrandId({});
      setLoading(false);
      return;
    }

    setLoading(true);
    const entries = await Promise.all(
      ids.map(async (brandId) => {
        const plan = await getWeeklyPlan(brandId, weekStart);
        return [brandId, plan] as const;
      })
    );
    setPlansByBrandId(Object.fromEntries(entries));
    setLoading(false);
  }, [brandIdsKey, getWeeklyPlan, weekStart]);

  useEffect(() => {
    void refreshPlans();
  }, [refreshPlans]);

  useEffect(() => {
    if (typeof window === "undefined" || !brandIdsKey) {
      return;
    }

    const brandIdSet = new Set(brandIdsKey.split("|"));
    const handleRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ brandId?: string }>).detail;
      if (!detail?.brandId || !brandIdSet.has(detail.brandId)) {
        return;
      }
      void refreshPlans();
    };

    window.addEventListener(WEEKLY_PLAN_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(WEEKLY_PLAN_REFRESH_EVENT, handleRefresh);
    };
  }, [brandIdsKey, refreshPlans]);

  return {
    plansByBrandId,
    loading,
    weekStart,
    refreshPlans,
  };
};

export default useWeeklyPlansForBrands;
